import { useEffect, useRef } from 'react';

import {
  MPR_AXES_SHORTCUT,
  MPR_ANNOTATION_SHORTCUTS,
  MPR_ANNOTATIONS_VISIBILITY_SHORTCUT,
  MPR_ARROW_CONTROL_SELECTOR,
  MPR_FINISH_ANNOTATION_SHORTCUT,
  MPR_TOOL_SHORTCUTS,
  MPR_SHARPNESS_SHORTCUT,
  type MPRCommands,
  type MPRAnnotationTool,
  type MPRTool,
} from '../config';
import { matchesMPRShortcut } from '../lib';

// Enter on a focused control must keep activating that control.
const ENTER_ACTIVATED_SELECTOR = 'button, [role="button"], a[href]';

type UseMPRShortcutsParams = {
  commands: MPRCommands;
  isEnabled: boolean;
};

const EDITABLE_SELECTOR = [
  'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="button"]):not([type="submit"]):not([type="reset"])',
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="textbox"]',
  '[role="combobox"]',
].join(', ');
const INTERACTIVE_SELECTOR = `${EDITABLE_SELECTOR}, input, button, a[href], [tabindex], [role="button"], [role="slider"], [role="dialog"], [role="menu"], [role="listbox"]`;

const isViewerVisible = (viewer: HTMLElement) => {
  const viewport = viewer.querySelector<HTMLElement>('[data-mpr-viewport]');
  const ownerWindow = viewer.ownerDocument.defaultView;
  if (
    !viewport ||
    !ownerWindow ||
    viewport.closest('[hidden], [inert], [aria-hidden="true"]')
  ) {
    return false;
  }

  const rect = viewport.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < ownerWindow.innerHeight &&
    rect.left < ownerWindow.innerWidth &&
    ownerWindow.getComputedStyle(viewport).visibility === 'visible'
  );
};

export const useMPRShortcuts = ({
  commands,
  isEnabled,
}: UseMPRShortcutsParams) => {
  const ref = useRef<HTMLDivElement>(null);
  const handledCodes = useRef<string[]>([]);
  const paramsRef = useRef({ commands, isEnabled });
  paramsRef.current = { commands, isEnabled };

  const handleKeyDown = (event: KeyboardEvent) => {
    const { commands, isEnabled } = paramsRef.current;
    const target = event.target;
    if (
      !isEnabled ||
      event.defaultPrevented ||
      event.isComposing ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey ||
      !(target instanceof Element) ||
      target.closest(EDITABLE_SELECTOR)
    ) {
      return;
    }

    const direction =
      event.code === 'ArrowUp' ? -1 : event.code === 'ArrowDown' ? 1 : null;
    if (direction && target.closest(MPR_ARROW_CONTROL_SELECTOR)) {
      return;
    }

    if (event.repeat && handledCodes.current.includes(event.code)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const tool = (Object.keys(MPR_TOOL_SHORTCUTS) as MPRTool[]).find(
      (candidate) => {
        const shortcut = MPR_TOOL_SHORTCUTS[candidate];
        return shortcut ? matchesMPRShortcut(shortcut, event.code) : false;
      },
    );
    const annotationTool = (
      Object.keys(MPR_ANNOTATION_SHORTCUTS) as MPRAnnotationTool[]
    ).find((candidate) =>
      matchesMPRShortcut(MPR_ANNOTATION_SHORTCUTS[candidate], event.code),
    );
    const imageCommands = commands.imageOptions;
    const keyboardCommands: Record<string, (() => boolean) | undefined> = {
      [MPR_AXES_SHORTCUT.code]: commands.toggleAxes,
      [MPR_ANNOTATIONS_VISIBILITY_SHORTCUT.code]:
        commands.annotations?.toggleVisibility,
      [MPR_SHARPNESS_SHORTCUT.code]: imageCommands
        ? () => imageCommands.selectTool('sharpness')
        : undefined,
      [MPR_FINISH_ANNOTATION_SHORTCUT.code]: target.closest(
        ENTER_ACTIVATED_SELECTOR,
      )
        ? undefined
        : commands.annotations?.finish,
      Escape: commands.annotations?.cancel,
      Delete: commands.annotations?.deleteSelected,
    };
    const handled = direction
      ? commands.stepSlice?.(direction)
      : !event.repeat &&
        (tool
          ? commands.selectTool(tool)
          : annotationTool
            ? commands.annotations?.selectTool(annotationTool)
            : keyboardCommands[event.code]?.());

    if (handled) {
      if (!direction) {
        handledCodes.current = [...handledCodes.current, event.code];
      }
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    handledCodes.current = handledCodes.current.filter(
      (code) => code !== event.code,
    );
  };

  useEffect(() => {
    const container = ref.current;
    if (!container) {
      return;
    }

    const modal = container.closest<HTMLElement>('[data-modal-root]');
    const ownerDocument = container.ownerDocument;
    const previousFocus = ownerDocument.activeElement;
    modal?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      const root = ownerDocument.fullscreenElement ?? ownerDocument.body;
      const modals = root.querySelectorAll(
        '[data-modal-root], [role="dialog"][aria-modal="true"]',
      );
      const topModal = modals[modals.length - 1];
      if (
        !paramsRef.current.isEnabled ||
        !(target instanceof Element) ||
        !isViewerVisible(container) ||
        (topModal ? topModal !== modal : Boolean(modal))
      ) {
        return;
      }

      const targetViewer = target.closest('[data-mpr-shortcuts]');
      if (targetViewer && targetViewer !== container) {
        return;
      }

      if (!container.contains(target)) {
        if (
          (modal && target !== ownerDocument.body && !modal.contains(target)) ||
          (target !== modal && target.closest(INTERACTIVE_SELECTOR))
        ) {
          return;
        }

        const viewers = Array.from(
          (modal ?? root).querySelectorAll<HTMLElement>(
            '[data-mpr-shortcuts="true"]',
          ),
        ).filter(isViewerVisible);
        if (viewers.length !== 1 || viewers[0] !== container) {
          return;
        }
      }

      handleKeyDown(event);
    };
    const clearHandledCodes = () => {
      handledCodes.current = [];
    };

    ownerDocument.addEventListener('keydown', onKeyDown);
    ownerDocument.addEventListener('keyup', onKeyUp);
    ownerDocument.defaultView?.addEventListener('blur', clearHandledCodes);

    return () => {
      ownerDocument.removeEventListener('keydown', onKeyDown);
      ownerDocument.removeEventListener('keyup', onKeyUp);
      ownerDocument.defaultView?.removeEventListener('blur', clearHandledCodes);
      clearHandledCodes();

      if (
        modal &&
        previousFocus instanceof HTMLElement &&
        previousFocus.isConnected &&
        (modal.contains(ownerDocument.activeElement) ||
          ownerDocument.activeElement === ownerDocument.body)
      ) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, []);

  return {
    ref,
    'data-mpr-shortcuts': isEnabled,
  };
};
